import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Image as ImageIcon,
    Pause as PauseIcon,
    PlayArrow as PlayIcon,
    Crop as CropIcon,
    AspectRatio as AspectRatioIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    CardMedia,
    Chip,
    Container,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Skeleton,
    Typography,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { getCarouselImages } from '../utils/carouselImages.ts';

// Styled components
const StyledCarouselSection = styled(Box)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  padding: theme.spacing(8, 0),
  backgroundColor: $isDarkMode ? theme.palette.background.default : '#f8f4f7',
}));

const StyledCarouselContainer = styled(Container)(({ theme }) => ({
  maxWidth: 'lg',
}));

const StyledCarouselHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const StyledCarouselTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

const StyledCarouselSubtitle = styled(Typography)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
}));

const StyledCarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '900px',
  margin: '0 auto',
}));

const StyledCarouselPaper = styled(Paper)<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  borderRadius: theme.spacing(1.5),
  overflow: 'hidden',
  backgroundColor: $isDarkMode ? theme.palette.background.paper : 'white',
  position: 'relative',
}));

const StyledCarouselMedia = styled('img')<{ $scalingMode: 'contain' | 'cover' }>(({ theme, $scalingMode }) => ({
  height: 500,
  width: '100%',
  objectFit: $scalingMode,
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5',
  transition: 'transform 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.02)',
    filter: 'brightness(1.1)',
  },
}));

const StyledNavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(255,255,255,0.9)',
  boxShadow: theme.shadows[4],
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,1)',
    transform: 'translateY(-50%) scale(1.1)',
  },
}));

const StyledPreviousButton = styled(StyledNavigationButton)({
  left: 16,
});

const StyledNextButton = styled(StyledNavigationButton)({
  right: 16,
});

const StyledControlsContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  display: 'flex',
  gap: theme.spacing(1),
}));

const StyledControlButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.9)',
  boxShadow: theme.shadows[4],
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,1)',
  },
}));

const StyledDotsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(3),
  gap: theme.spacing(1),
}));

const StyledDot = styled(Box)<{ $active: boolean }>(({ theme, $active }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: $active ? theme.palette.primary.main : theme.palette.grey[400],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: $active ? theme.palette.primary.dark : theme.palette.grey[600],
    transform: 'scale(1.2)',
  },
}));

const StyledEllipsisDot = styled(Box)(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: theme.palette.grey[400],
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '8px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.grey[600],
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledDialogImage = styled('img')<{ $isDarkMode: boolean }>(({ theme, $isDarkMode }) => ({
  width: '100%',
  height: 'auto',
  maxHeight: '90vh',
  objectFit: 'contain',
  backgroundColor: $isDarkMode ? '#1a1a1a' : '#000',
  borderRadius: theme.spacing(0.5),
}));

const StyledDialogNavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0,0,0,0.7)',
  color: 'white',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.9)',
  },
}));

const StyledDialogLeftButton = styled(StyledDialogNavigationButton)({
  left: 16,
});

const StyledDialogRightButton = styled(StyledDialogNavigationButton)({
  right: 16,
});

const StyledDialogCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  backgroundColor: 'rgba(255,255,255,0.9)',
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,1)',
  },
}));

const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
}));

const StyledImageIcon = styled(ImageIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  verticalAlign: 'middle',
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StyledEmptyContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
}));

const StyledImageCounter = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    maxHeight: '90vh',
  },
}));

export const SmartCarousel: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [images, setImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [autoPlayInterval, setAutoPlayInterval] = useState<NodeJS.Timeout | null>(null);
  const [scalingMode, setScalingMode] = useState<'contain' | 'cover'>('contain');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
      }, 4000); // Change image every 4 seconds

      setAutoPlayInterval(interval);
      return () => clearInterval(interval);
    } else if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      setAutoPlayInterval(null);
    }
  }, [isAutoPlaying, images.length]);

  // Load images from carouselImages folder
  useEffect(() => {
    const loadCarouselImages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load images using the utility function
        const carouselImages = await getCarouselImages();
        setImages(carouselImages);
        setLoading(false);
      } catch (err) {
        console.error('Error loading carousel images:', err);
        setError('Er is een fout opgetreden bij het laden van de afbeeldingen.');
        setLoading(false);
      }
    };

    loadCarouselImages();
  }, []);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(!isAutoPlaying);
  };

  const toggleScalingMode = () => {
    setScalingMode(scalingMode === 'contain' ? 'cover' : 'contain');
  };

  const handleImageClick = () => {
    // Pause auto-play when image is clicked
    setIsAutoPlaying(false);
    // Open dialog
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    // Resume auto-play when dialog is closed
    setIsAutoPlaying(true);
  };

  if (loading) {
    return (
      <StyledCarouselSection component="section" $isDarkMode={isDarkMode}>
        <StyledCarouselContainer>
          <StyledCarouselHeader>
            <StyledCarouselTitle variant="h4" component="h2">
              <StyledImageIcon />
              Foto Galerij
            </StyledCarouselTitle>
            <StyledCarouselSubtitle variant="body1">
              Laden van afbeeldingen...
            </StyledCarouselSubtitle>
          </StyledCarouselHeader>
          
          <StyledLoadingContainer>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" width={300} height={400} />
            ))}
          </StyledLoadingContainer>
        </StyledCarouselContainer>
      </StyledCarouselSection>
    );
  }

  if (error) {
    return (
      <StyledCarouselSection component="section" $isDarkMode={isDarkMode}>
        <StyledCarouselContainer>
          <StyledAlert severity="error">
            {error}
          </StyledAlert>
        </StyledCarouselContainer>
      </StyledCarouselSection>
    );
  }

  if (images.length === 0) {
    return (
      <StyledCarouselSection component="section" $isDarkMode={isDarkMode}>
        <StyledCarouselContainer>
          <StyledEmptyContainer>
            <Typography variant="h6" color="text.secondary">
              Geen afbeeldingen gevonden in de carousel map.
            </Typography>
          </StyledEmptyContainer>
        </StyledCarouselContainer>
      </StyledCarouselSection>
    );
  }

  return (
    <StyledCarouselSection component="section" $isDarkMode={isDarkMode}>
      <StyledCarouselContainer>
        <StyledCarouselHeader>
          <StyledCarouselTitle variant="h4" component="h2">
            <ImageIcon />
            Foto Galerij
          </StyledCarouselTitle>
          <StyledCarouselSubtitle variant="body1">
            Bekijk de mooiste momenten van Pippaya Hondenopvang
          </StyledCarouselSubtitle>
        </StyledCarouselHeader>

        {/* Carousel Container */}
        <StyledCarouselWrapper>
          <StyledCarouselPaper $isDarkMode={isDarkMode} elevation={8}>
            {/* Image Display */}
            <StyledCarouselMedia
              src={images[currentIndex]}
              alt={`Pippaya Hondenopvang - Foto ${currentIndex + 1}`}
              onClick={handleImageClick}
              $scalingMode={scalingMode}
            />

            {/* Navigation Buttons */}
            <StyledPreviousButton onClick={handlePrevious}>
              <ChevronLeftIcon />
            </StyledPreviousButton>

            <StyledNextButton onClick={handleNext}>
              <ChevronRightIcon />
            </StyledNextButton>

            {/* Control Buttons */}
            <StyledControlsContainer>
              {/* Scaling Toggle */}
              <StyledControlButton
                onClick={toggleScalingMode}
                title={scalingMode === 'contain' ? 'Schakel naar crop modus' : 'Schakel naar fit modus'}
              >
                {scalingMode === 'contain' ? <CropIcon /> : <AspectRatioIcon />}
              </StyledControlButton>

              {/* Auto-play Toggle */}
              {images.length > 1 && (
                <StyledControlButton
                  onClick={toggleAutoPlay}
                  title={isAutoPlaying ? 'Pause auto-play' : 'Start auto-play'}
                >
                  {isAutoPlaying ? <PauseIcon /> : <PlayIcon />}
                </StyledControlButton>
              )}
            </StyledControlsContainer>
          </StyledCarouselPaper>

          {/* Dots Indicator */}
          {images.length > 1 && (
            <StyledDotsContainer>
              {(() => {
                const maxDots = 5;
                const totalImages = images.length;
                
                if (totalImages <= maxDots) {
                  // Show all dots if we have 5 or fewer images
                  return images.map((_, index) => (
                    <StyledDot
                      key={index}
                      onClick={() => handleDotClick(index)}
                      $active={index === currentIndex}
                    />
                  ));
                } else {
                  // Show paginated dots for more than 5 images
                  const startIndex = Math.max(0, Math.min(currentIndex - 2, totalImages - maxDots));
                  const endIndex = startIndex + maxDots;
                  
                  return (
                    <>
                      {startIndex > 0 && (
                        <StyledEllipsisDot
                          onClick={() => handleDotClick(Math.max(0, startIndex - 1))}
                        >
                          ...
                        </StyledEllipsisDot>
                      )}
                      
                      {Array.from({ length: maxDots }, (_, i) => {
                        const dotIndex = startIndex + i;
                        return (
                          <StyledDot
                            key={dotIndex}
                            onClick={() => handleDotClick(dotIndex)}
                            $active={dotIndex === currentIndex}
                          />
                        );
                      })}
                      
                      {endIndex < totalImages && (
                        <StyledEllipsisDot
                          onClick={() => handleDotClick(Math.min(totalImages - 1, endIndex))}
                        >
                          ...
                        </StyledEllipsisDot>
                      )}
                    </>
                  );
                }
              })()}
            </StyledDotsContainer>
          )}

          {/* Image Counter */}
          <StyledImageCounter>
            <Chip 
              icon={<ImageIcon />} 
              label={`${currentIndex + 1} / ${images.length}`} 
              size="small" 
              variant="outlined"
              color="primary"
            />
          </StyledImageCounter>
        </StyledCarouselWrapper>

        {/* Full-screen Image Dialog */}
        <StyledDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          maxWidth="lg"
          fullWidth
        >
          <StyledDialogContent>
            {/* Close Button */}
            <StyledDialogCloseButton
              onClick={handleDialogClose}
            >
              <CloseIcon />
            </StyledDialogCloseButton>

            {/* Full-screen Image */}
            <StyledDialogImage
              src={images[currentIndex]}
              alt={`Pippaya Hondenopvang - Foto ${currentIndex + 1}`}
              $isDarkMode={isDarkMode}
            />

            {/* Navigation in Dialog */}
            {images.length > 1 && (
              <>
                <StyledDialogLeftButton onClick={handlePrevious}>
                  <ChevronLeftIcon />
                </StyledDialogLeftButton>

                <StyledDialogRightButton onClick={handleNext}>
                  <ChevronRightIcon />
                </StyledDialogRightButton>
              </>
            )}
          </StyledDialogContent>
        </StyledDialog>
      </StyledCarouselContainer>
    </StyledCarouselSection>
  );
}; 